import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useConfirm } from '../components/ConfirmModal'
import { supabase } from '../lib/supabase'
import { db } from '../lib/db'
import { SOCIAL_PROVIDERS, enabledSocialProviders, socialButtonStyle } from '../lib/socialProviders'
import SocialGlyph from '../components/SocialGlyph'

// Normalize whatever the DB returned for birthdate into the YYYY-MM-DD the
// HTML <input type="date"> expects. postgres.js can return DATE as a Date
// object OR an ISO-ish string depending on driver settings.
function birthdateToInputValue(b) {
  if (!b) return ''
  if (b instanceof Date) return b.toISOString().split('T')[0]
  if (typeof b === 'string' && b.includes('T')) return b.split('T')[0]
  return b
}

// Keep a surfaced error readable in the small toast — long provider/SDK
// messages get an ellipsis instead of overflowing the strip.
function truncate(s, max) {
  const str = String(s ?? '')
  return str.length > max ? `${str.slice(0, max - 1)}…` : str
}

export default function ProfileEditPage() {
  const { player, refreshPlayer, prepareLinkIdentity } = useAuth()
  const { confirm } = useConfirm()
  const navigate = useNavigate()

  // SINGLE-CLICK social link (mobile-safe). `linking` is the provider id whose
  // OAuth URL is being prepared (button shows a spinner); one tap both prepares
  // the URL AND navigates to the same-origin bridge on success — no second tap.
  // `unlinking` is the provider id whose identity is being removed.
  const [linking, setLinking] = useState(null)     // provider id | null
  const [unlinking, setUnlinking] = useState(null) // provider id | null
  // The user's linked social identities (from supabase.auth.getUserIdentities).
  // `null` means "still loading" — render a placeholder rather than flashing the
  // wrong (Link vs Unlink) button; `[]` means loaded-with-none.
  const [identities, setIdentities] = useState(null) // Identity[] | null
  // Link/unlink errors render inside the Linked Accounts card (near the buttons
  // the user tapped), NOT in the profile-info card's `toast` — on mobile that
  // card is scrolled off-screen, so a failure surfaced there is invisible.
  const [linkError, setLinkError] = useState(null)  // string | null

  const [firstName, setFirstName] = useState(player?.first_name || '')
  const [lastName, setLastName] = useState(player?.last_name || '')
  const [name, setName] = useState(player?.name || '')
  const [birthdate, setBirthdate] = useState(birthdateToInputValue(player?.birthdate))
  const [phone, setPhone] = useState(player?.phone || '')
  const [email, setEmail] = useState(player?.email || '')
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState(null)

  const canSave = name.trim().length >= 2

  // Which social providers render is driven ENTIRELY by the shared ENABLED_SOCIAL
  // list (src/lib/socialProviders.js). When it is empty (T3-hide-socials, App
  // Store 4.8) the entire "Linked Accounts" card is suppressed — heading, copy,
  // loading placeholder, and buttons — so no orphaned social UX remains, matching
  // how PlayerLoginPage gates its whole social block behind social.length > 0.
  const socialProviders = enabledSocialProviders()
  const hasSocialProviders = socialProviders.length > 0

  const handleSave = async () => {
    if (!canSave || saving) return
    setSaving(true)
    try {
      await db('update_player_profile', {
        player_id: player.id,
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        name: name.trim(),
        birthdate: birthdate || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
      })
      if (refreshPlayer) await refreshPlayer()
      setToast('Profile updated!')
      setTimeout(() => setToast(null), 2500)
    } catch {
      setToast('Failed to save')
      setTimeout(() => setToast(null), 3000)
    }
    setSaving(false)
  }

  // Load the user's linked social identities so we can show a Link button for
  // unlinked providers and an Unlink button for linked ones. Called on mount and
  // after every successful unlink (a successful LINK does a full-page redirect,
  // so it re-runs this on the fresh mount when the provider returns to /profile-edit).
  const loadIdentities = async () => {
    try {
      const { data, error } = await supabase.auth.getUserIdentities()
      if (error) {
        // Non-fatal: treat as "none loaded" so the Link buttons still render.
        // eslint-disable-next-line no-console
        console.error('getUserIdentities failed', error)
        setIdentities([])
        return
      }
      setIdentities(data?.identities || [])
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('getUserIdentities threw', e)
      setIdentities([])
    }
  }

  useEffect(() => {
    // Skip the getUserIdentities() network call entirely when no social
    // providers are enabled — the card that consumes `identities` never renders.
    if (hasSocialProviders) loadIdentities()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // SINGLE-CLICK link. One tap PREPARES the provider's OAuth URL through
  // supabase.auth.linkIdentity (via AuthContext's prepareLinkIdentity — which
  // attaches the provider to the SAME auth.users record; no second auth_user_id,
  // that column is UNIQUE) AND, on success, navigates SAME-TAB to the same-origin
  // oauth-bridge, which 302s onward to the provider.
  //
  // WHY the bridge (and why one tap now works): a same-tab nav STRAIGHT to
  // facebook.com is blocked by mobile anti-tracking (proven on-device, Android) —
  // it hangs. That was why the old flow needed a two-tap "Prepare → Continue"
  // dance (a real user click on an <a>). But the bridge is SAME-ORIGIN, which is
  // NOT anti-tracking-blocked, so a programmatic `window.location.href` to it
  // after the async prepare is fine — mirroring the working login path
  // (signInWithOAuth hops through supabase.co first). /.netlify/ also bypasses
  // the SW nav cache + navigateFallback. Full-page round-trip: the provider
  // returns to /profile-edit and the app re-inits with the new avatar, like login.
  // Provider-agnostic — the shared ENABLED_SOCIAL list decides which buttons
  // render (D-283).
  const handleLink = async (provider) => {
    if (linking) return
    setLinkError(null)
    setLinking(provider)
    const name = SOCIAL_PROVIDERS[provider]?.displayName || provider
    const { url, error } = await prepareLinkIdentity(provider)
    if (error || !url) {
      // Fail LOUD, never silent: surface the ACTUAL provider/SDK error — and
      // render it inside the Linked Accounts card, where the user tapped.
      // eslint-disable-next-line no-console
      console.error('prepareLinkIdentity failed', provider, error)
      setLinkError(`Couldn't link ${name}: ${truncate(error || 'No link URL returned', 120)}`)
      setTimeout(() => setLinkError(null), 6000)
      setLinking(null)
      return
    }
    // Success: navigate SAME-TAB to the same-origin bridge, which 302s onward to
    // the provider. Do NOT clear `linking` — the full-page navigation is now
    // underway and this component is about to unmount.
    window.location.href = `/.netlify/functions/oauth-bridge?to=${encodeURIComponent(url)}`
  }

  // UNLINK a linked social provider so a user can undo/re-test a link. Confirms
  // via the shared ConfirmModal, calls supabase.auth.unlinkIdentity with the
  // provider's identity object, then re-fetches identities + refreshes the
  // player. Supabase enforces "must have at least one other identity" (e.g. it
  // refuses to remove the only remaining identity when phone is not counted) —
  // surface that ACTUAL message in the red toast rather than swallowing it.
  //
  // NOTE: we deliberately do NOT clear players.avatar_url on unlink — re-linking
  // overwrites it, and clearing it would require a server (DB-write) endpoint we
  // don't have here. Avatar-clear-on-unlink is a possible future follow-up.
  const handleUnlink = async (provider, identity) => {
    if (unlinking) return
    const name = SOCIAL_PROVIDERS[provider]?.displayName || provider
    const ok = await confirm({
      title: `Unlink ${name}?`,
      message: 'You can re-link anytime.',
      ok: 'Unlink',
      cancel: 'Cancel',
      danger: true,
    })
    if (!ok) return
    setLinkError(null)
    setUnlinking(provider)
    try {
      const { error } = await supabase.auth.unlinkIdentity(identity)
      if (error) throw new Error(error.message)
      await loadIdentities()
      if (refreshPlayer) await refreshPlayer()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('unlinkIdentity failed', provider, e)
      setLinkError(`Couldn't unlink ${name}: ${truncate(e?.message || 'Unlink failed', 120)}`)
      setTimeout(() => setLinkError(null), 6000)
    }
    setUnlinking(null)
  }

  const avatarUrl = player?.avatar_url || null

  return (
    <div className="page">
      <div className="container">
        <div style={{ paddingTop: 8, marginBottom: 20 }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => navigate('/me')}
            style={{ marginBottom: 12, padding: '6px 12px' }}
          >
            ← Back
          </button>
          <h1 style={{ fontSize: 'clamp(1.4rem, 4vw, 1.8rem)' }}>Edit Profile</h1>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          {avatarUrl && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <img
                src={avatarUrl}
                alt="Profile"
                referrerPolicy="no-referrer"
                style={{
                  width: 72, height: 72, borderRadius: '50%',
                  objectFit: 'cover', border: '2px solid var(--gold)',
                }}
              />
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label>First Name</label>
            <input
              className="input"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              placeholder="Your first name"
              autoComplete="given-name"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label>Last Name</label>
            <input
              className="input"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              placeholder="Your last name"
              autoComplete="family-name"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label>Display Name</label>
            <input
              className="input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Shown on scorecards and leaderboards"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label>Birthdate</label>
            <input
              className="input"
              value={birthdate}
              onChange={e => setBirthdate(e.target.value)}
              type="date"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label>Phone</label>
            <input
              className="input"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="Phone number"
              type="tel"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label>Email</label>
            <input
              className="input"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Email address"
              type="email"
            />
          </div>

          <button
            className="btn btn-primary btn-full"
            onClick={handleSave}
            disabled={!canSave || saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>

          {toast && <div className="toast success" style={{ marginTop: 10 }}>{toast}</div>}
        </div>

        {hasSocialProviders && (
        <div className="card">
          <h3 style={{ marginBottom: 4 }}>Linked Accounts</h3>
          <p className="text-muted" style={{ fontSize: '0.75rem', marginBottom: 12 }}>
            Link a social account to use its profile picture. Phone remains your
            primary sign-in.
          </p>
          {identities === null ? (
            // Still loading identities — render a placeholder so we never flash
            // the wrong (Link vs Unlink) button before we know the link state.
            <p className="text-muted" style={{ fontSize: '0.8rem' }}>
              Loading linked accounts…
            </p>
          ) : (
            socialProviders.map((sp, i, arr) => {
              const spacing = i < arr.length - 1 ? { marginBottom: 8 } : {}
              const identity = identities.find((id) => id.provider === sp.provider)
              const isLinked = !!identity
              const isLinking = linking === sp.provider
              const isUnlinking = unlinking === sp.provider

              // LINKED: show a secondary/ghost "Unlink {name}" control so the
              // link can be undone / re-tested. Confirms via ConfirmModal.
              if (isLinked) {
                return (
                  <button
                    key={sp.provider}
                    className="btn btn-ghost btn-full"
                    onClick={() => handleUnlink(sp.provider, identity)}
                    disabled={!!unlinking}
                    style={spacing}
                  >
                    {isUnlinking ? 'Unlinking…' : `Unlink ${sp.displayName}`}
                  </button>
                )
              }

              // UNLINKED: the single-click Link button. One tap prepares the
              // OAuth URL and, on success, navigates to the same-origin bridge.
              return (
                <button
                  key={sp.provider}
                  className="btn btn-full"
                  onClick={() => handleLink(sp.provider)}
                  disabled={!!linking}
                  style={{ ...socialButtonStyle(sp.provider), ...spacing }}
                >
                  <SocialGlyph provider={sp.provider} />
                  {isLinking ? 'Linking…' : sp.profileLabel}
                </button>
              )
            })
          )}
          {linkError && (
            <div className="toast error" style={{ marginTop: 10 }} role="alert">
              {linkError}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  )
}
